import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

export default function ExitIntentPopup() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasShown, setHasShown] = useState(false);

  const handleMouseLeave = useCallback(
    (e: MouseEvent) => {
      if (hasShown) return;
      if (e.clientY <= 5) {
        setIsVisible(true);
        setHasShown(true);
        sessionStorage.setItem('exitIntentShown', 'true');
      }
    },
    [hasShown]
  );

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem('exitIntentShown');
    if (alreadyShown) {
      setHasShown(true);
      return;
    }

    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 3000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [handleMouseLeave]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      style={{ background: 'rgba(10, 15, 40, 0.75)', backdropFilter: 'blur(6px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl overflow-hidden"
        style={{ animation: 'exitIntentIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top accent bar */}
        <div className="h-1.5 w-full" style={{ background: 'linear-gradient(90deg, #384774, #18c7f4)' }} />

        {/* Close button */}
        <button
          onClick={handleClose}
          aria-label="Fechar"
          className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-gray-500 dark:text-gray-400 text-lg"></i>
        </button>

        {/* Content */}
        <div className="px-10 pt-9 pb-10">
          {/* Icon badge */}
          <div
            className="w-16 h-16 flex items-center justify-center rounded-2xl mb-6"
            style={{ background: 'linear-gradient(135deg, #384774, #18c7f4)' }}
          >
            <i className="ri-headphone-line text-white text-3xl"></i>
          </div>

          {/* Headline */}
          <p className="font-body font-semibold text-sm uppercase tracking-widest mb-2" style={{ color: '#18c7f4' }}>
            Espere! Não vá ainda
          </p>
          <h2 className="font-heading font-bold text-3xl text-secondary dark:text-white mb-3 leading-tight">
            Seu evento merece a melhor tecnologia de áudio
          </h2>
          <p className="font-body text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-8">
            Somos especialistas em <strong className="text-secondary dark:text-white">palestra silenciosa</strong> desde 2007, com sede em São Paulo. Fale com um especialista agora mesmo e receba um orçamento personalizado em minutos.
          </p>

          {/* Stats strip */}
          <div className="flex items-center justify-between mb-8 py-4 border-y border-gray-100 dark:border-gray-800">
            {[
              { value: '500+', label: 'Eventos' },
              { value: '150K+', label: 'Participantes' },
              { value: '98%', label: 'Satisfação' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-heading font-black text-2xl" style={{ color: '#18c7f4' }}>{stat.value}</p>
                <p className="font-body text-xs text-gray-500 dark:text-gray-400 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <a
              href="https://wa.me/5511917776227?text=Olá! Gostaria de um orçamento para meu evento."
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-full font-body font-bold text-sm uppercase transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-white"
              style={{ background: '#25D366' }}
            >
              <i className="ri-whatsapp-line text-lg"></i>
              Falar no WhatsApp
            </a>
            <Link
              to="/contato"
              onClick={handleClose}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-full font-body font-bold text-sm uppercase transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-white"
              style={{ background: '#384774' }}
            >
              <i className="ri-mail-send-line text-lg"></i>
              Pedir Orçamento
            </Link>
          </div>

          {/* Dismiss link */}
          <p className="text-center mt-5">
            <button
              onClick={handleClose}
              className="font-body text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer underline-offset-2 hover:underline"
            >
              Não, obrigado. Sair sem orçamento.
            </button>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes exitIntentIn {
          from { opacity: 0; transform: scale(0.88) translateY(20px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
}
